import type { Company } from "@repo/app-types/entities/company";
import type {
  Project,
  ProjectLinkAccess,
  ProjectUpdate,
} from "@repo/app-types/entities/project";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
  projectLinkAccessSchema,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { getCompanyHasFeatureAccess } from "@repo/app-types/helpers/billingFeatureGating";
import {
  convertToValidHttpsUrl,
  getIsTrainingProject,
  isRecruitingModeProject,
} from "@repo/app-types/helpers/utilities";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import { IconLock } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { useUpdateJobTypeMutation } from "../../../api/entities/jobType.js";
import { appPaths } from "../../../app/appPaths.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { UpgradeText } from "../../billing/upgradeText.js";
import { WelcomeAndOutroMessagesEditor } from "./welcomeAndOutroMessagesEditor.js";

interface AppearanceSettingsSectionProps {
  onSaveProject: (update: ProjectUpdate) => void;
  company: Company;
  project: Project;
}

export const AppearanceSettingsSection: FC<AppearanceSettingsSectionProps> = ({
  onSaveProject,
  company,
  project,
}) => {
  const navigate = useNavigate();
  const [updateJobType] = useUpdateJobTypeMutation();
  const isTrainingProject = getIsTrainingProject(project);
  const linkObjectLabel = isTrainingProject ? "Learning journey" : "Interview";

  const currentRedirectUrl =
    getProjectAndJobTypeProperty(project, JobTypeProperty.RedirectUrl) ?? "";
  const [redirectUrl, setRedirectUrl] = useState(currentRedirectUrl);
  const [redirectUrlError, setRedirectUrlError] = useState("");

  const welcomeMessage = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.WelcomeMessage,
  );
  const outroMessage = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.OutroMessage,
  );
  const [shouldShowOverrideSettings, setShouldShowOverrideSettings] = useState(
    Boolean(welcomeMessage || outroMessage),
  );

  const isMissingFeatureAccess = !getCompanyHasFeatureAccess(
    company,
    "whitelabeling",
  );

  const [linkAccess, setLinkAccess] = useState(project.settings.link_access);

  const onLinkAccessChange = (newValue: string) => {
    const newLinkAccess = projectLinkAccessSchema.parse(newValue);
    setLinkAccess(newLinkAccess);
    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        link_access: newLinkAccess,
      },
    });
  };

  const onClickSaveRedirectUrl = () => {
    setRedirectUrlError("");

    if (!redirectUrl) {
      onSaveProject({
        id: project.id,
        settings: {
          ...project.settings,
          redirect_url: null,
        },
      });

      if (project.job_type) {
        void updateJobType({
          id: project.job_type.id,
          settings: {
            ...project.job_type.settings,
            redirect_url: null,
          },
        });
      }
      return;
    }

    const url = convertToValidHttpsUrl(redirectUrl);

    if (!url) {
      setRedirectUrlError(
        "Invalid URL - please provide a valid URL starting with https://",
      );
      return;
    }

    setRedirectUrl(url);
    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        redirect_url: url,
      },
    });

    if (project.job_type) {
      void updateJobType({
        id: project.job_type.id,
        settings: {
          ...project.job_type.settings,
          redirect_url: url,
        },
      });
    }
  };

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <div>
          {!isRecruitingModeProject(project) ? (
            <Label size={Size.LARGE}>{linkObjectLabel} link settings</Label>
          ) : null}

          {isTrainingProject ? (
            <Can
              I={PermissionActions.UPDATE}
              a={PermissionSubjects.JOB_OPENINGS}
              passThrough={true}
            >
              {(allowed) => (
                <div className="mt-4 space-y-2">
                  <Label size={Size.SMALL}>Training access</Label>

                  <SimpleRadioGroup
                    value={linkAccess}
                    onChange={onLinkAccessChange}
                  >
                    <SimpleRadioOption
                      value={"default" satisfies ProjectLinkAccess}
                      isDisabled={!allowed}
                    >
                      Anyone with the {linkObjectLabel.toLowerCase()} link
                    </SimpleRadioOption>

                    <SimpleRadioOption
                      value={"existing-only" satisfies ProjectLinkAccess}
                      isDisabled={!allowed}
                    >
                      Only pre-registered participants and team members logged
                      into Alpharun
                    </SimpleRadioOption>
                  </SimpleRadioGroup>
                </div>
              )}
            </Can>
          ) : null}

          <div className="mt-4">
            <Label size={Size.SMALL}>Logo and background color</Label>
            <div className="mt-2 text-sm text-muted-foreground">
              Add a logo and background color in{" "}
              <span
                className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                onClick={() => {
                  navigate(appPaths.settingsOrganization.build());
                }}
              >
                organization settings
              </span>
              .
            </div>
          </div>

          <div className="mt-4">
            <Label size={Size.SMALL}>Redirect URL</Label>
            <div className="mt-2">
              <Input
                value={redirectUrl}
                onChange={(evt) => {
                  setRedirectUrlError("");
                  setRedirectUrl(evt.target.value);
                }}
                placeholder="https://example.com"
                errorDescription={redirectUrlError}
                inputClassName="max-w-[300px]"
                leftIcon={
                  isMissingFeatureAccess ? (
                    <IconLock className="h-4 w-4 text-gray-700" />
                  ) : undefined
                }
                isDisabled={isMissingFeatureAccess || !allowed}
                maxLength={200}
              />
            </div>
            {redirectUrl !== currentRedirectUrl ? (
              <SaveAndCancelButtons
                onSave={onClickSaveRedirectUrl}
                onCancel={() => {
                  setRedirectUrl(currentRedirectUrl);
                  setRedirectUrlError("");
                }}
              />
            ) : null}
            <div className="mt-2 text-sm text-muted-foreground">
              Participants will be redirected to this URL after they complete
              their interview.
              {isMissingFeatureAccess ? (
                <UpgradeText planFeatureName="whitelabeling" />
              ) : null}
            </div>
          </div>

          {shouldShowOverrideSettings ? (
            <WelcomeAndOutroMessagesEditor
              welcomeMessage={welcomeMessage}
              outroMessage={outroMessage}
              onWelcomeMessageSave={(message) => {
                onSaveProject({
                  id: project.id,
                  settings: {
                    ...project.settings,
                    welcome_message: message,
                  },
                });

                if (project.job_type) {
                  void updateJobType({
                    id: project.job_type.id,
                    settings: {
                      ...project.job_type.settings,
                      welcome_message: message,
                    },
                  });
                }
              }}
              onOutroMessageSave={(message) => {
                onSaveProject({
                  id: project.id,
                  settings: {
                    ...project.settings,
                    outro_message: message,
                  },
                });

                if (project.job_type) {
                  void updateJobType({
                    id: project.job_type.id,
                    settings: {
                      ...project.job_type.settings,
                      outro_message: message,
                    },
                  });
                }
              }}
              isProjectLevel={true}
              isDisabled={!allowed}
            />
          ) : (
            <div className="mt-4">
              <Label size={Size.SMALL}>Welcome and outro message</Label>
              <div className="mt-2 text-sm text-muted-foreground">
                Add a welcome and outro message in{" "}
                <span
                  className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                  onClick={() => {
                    navigate(appPaths.settingsOrganization.build());
                  }}
                >
                  organization settings
                </span>
                {allowed && (
                  <>
                    , or{" "}
                    <span
                      className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                      onClick={() => setShouldShowOverrideSettings(true)}
                    >
                      customize the message for this{" "}
                      {isRecruitingModeProject(project)
                        ? "job opening"
                        : "project"}
                    </span>
                  </>
                )}
                .
              </div>
            </div>
          )}
        </div>
      )}
    </Can>
  );
};
